import React from "react";
import "./ProductCard.css";
import { useNavigate } from "react-router-dom";
import useCart from "../../hooks/useCart";
import useWishlist from "../../hooks/useWishlist";
import { FaHeart, FaRegHeart } from "react-icons/fa";

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [addedToCart, setAddedToCart] = React.useState(false);
  const [inWishlist, setInWishlist] = React.useState(false);
  const videoRef = React.useRef(null);

  // Helper function to determine if URL is a video
  const isVideo = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Safe video play function with error handling
  const safePlayVideo = async (videoElement) => {
    if (!videoElement) return;
    
    try {
      await videoElement.play();
    } catch (error) {
      // Silently handle play errors (like when video is removed from DOM)
      console.log('Video play prevented:', error.message);
    }
  };

  // Safe video pause function
  const safePauseVideo = (videoElement) => {
    if (!videoElement) return;
    
    try {
      videoElement.pause();
    } catch (error) {
      console.log('Video pause prevented:', error.message);
    }
  };

  // Cleanup function for video
  const cleanupVideo = () => {
    if (videoRef.current) {
      safePauseVideo(videoRef.current);
      videoRef.current = null;
    }
  };

  React.useEffect(() => {
    if (product && product._id) {
      setInWishlist(isInWishlist(product._id));
    }
  }, [product, isInWishlist]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      cleanupVideo();
    };
  }, []);

  const hasDiscount =
    product.price && product.offerPrice && product.price > product.offerPrice;

  const discountPercentage = hasDiscount
    ? Math.round(((product.price - product.offerPrice) / product.price) * 100)
    : 0;
  const requiresSizeSelection = Array.isArray(product.availableSizes) && product.availableSizes.length > 0;

  return (
    <div
      className="product-card-container"
      onClick={() => navigate(`/product/${product._id}`)}
    >
      {product.badge && <span className="badge">{product.badge}</span>}
      
      <div 
        className="wishlist-icon"
        onClick={(e) => {
          e.stopPropagation();
          if (inWishlist) {
            removeFromWishlist(product._id);
            setInWishlist(false);
          } else {
            const success = addToWishlist(product);
            if (success) {
              setInWishlist(true);
            }
          }
        }}
      >
        {inWishlist ? <FaHeart className="heart-filled" /> : <FaRegHeart />}
      </div>

      <div className="product-image">
        {isVideo(product.images?.[0]) ? (
          <div className="video-thumbnail-wrapper">
            <video
              ref={videoRef}
              src={product.images?.[0]}
              className="product-video-thumbnail"
              muted
              onMouseEnter={(e) => {
                videoRef.current = e.target;
                safePlayVideo(e.target);
              }}
              onMouseLeave={(e) => {
                safePauseVideo(e.target);
                if (videoRef.current === e.target) {
                  videoRef.current = null;
                }
              }}
              onError={(e) => {
                console.log('Video error:', e.target.error);
              }}
            />
            <div className="video-play-indicator">🎥</div>
          </div>
        ) : (
          <img src={product.images?.[0]} alt={product.title} />
        )}
      </div>

      <div className="product-info">
        <div className="rating">
          <span>
            ★ {product.rating || "4.5"} ({product.reviews || "34"})
          </span>
        </div>

        <div className="product-pricing">
          <span className="discounted-pricee">₹{product.offerPrice}</span>
          {product.price && product.price !== product.offerPrice && (
            <>
              <span className="original-pricee">₹{product.price}</span>
              <span className="discount-tag">{discountPercentage}% Off</span>
            </>
          )}
        </div>

        <h3 className="product-name">{product.title}</h3>

        {product.offerPrice && (
          <div className="offer-pricee">
            Get it for ₹{product.offerPrice}{" "}
            <span className="coupon-textt">with Discount</span>
          </div>
        )}

        {/* Cart Button Logic */}
        {isInCart(product._id) ? (
          <button
            className="add-to-cart1"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/cart");
            }}
          >
            Go to Cart
          </button>
        ) : (
          <button
            className="add-to-cart1"
            onClick={(e) => {
              e.stopPropagation();
              if (requiresSizeSelection) {
                navigate(`/product/${product._id}`);
                return;
              }
              const success = addToCart(product, 1);
              if (success) {
                setAddedToCart(true);
                setTimeout(() => setAddedToCart(false), 2000);
              }
            }}
          >
            {requiresSizeSelection ? "Select Size" : addedToCart ? "Added to Cart!" : "Add to Cart"}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
